"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/card"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Textarea } from "src/components/ui/textarea"

export default function FeedbackForm() {
    const [submitted, setSubmitted] = useState(false)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false) // Added for submit button loading
    const [error, setError] = useState(null)
    const [questions, setQuestions] = useState([])
    const [passageData, setPassageData] = useState(null)
    const [formData, setFormData] = useState({
        traineeName: "",
        traineeId: "",
    })
    const [hasPreviousAttempt, setHasPreviousAttempt] = useState(false)
    const [courseDateExpired, setCourseDateExpired] = useState(false)

    // Extract id from URL (654 from http://localhost:3000/3d/pcc/654)
    const getUrlId = () => {
        const pathParts = window.location.pathname.split('/')
        return pathParts[pathParts.length - 1]
    }

    // Function to check if course date has passed
    const checkCourseDate = (courseDate) => {
        if (!courseDate) return false;

        try {
            // Parse DD-MM-YYYY format
            const [day, month, year] = courseDate.split('-').map(Number);
            const courseEndDate = new Date(year, month - 1, day); // month is 0-indexed

            // Get current date (at start of day for comparison)
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            courseEndDate.setHours(0, 0, 0, 0);

            return currentDate > courseEndDate;
        } catch (err) {
            console.error("Error parsing course date:", err);
            return false;
        }
    }

    // Fetch questions based on URL id
    const fetchQuestions = async () => {
        try {
            const id = getUrlId()
            const payload = {
                "target": "pccanswer",
                "action": "search",
                "filters": {
                    "logicalOperator": "AND",
                    "rules": [
                        {
                            "field": "id",
                            "dataType": "tag",
                            "comparisonOperator": "equals",
                            "value": id
                        }
                    ]
                },
                "columnsToRetrieve": [],
                "resultDisplayOrder": "row",
                "maxRowsPerPage": 10,
                "pageToRetrieve": 1
            }

            const response = await fetch("https://stg-bex.coolriots.ai/bexo/bexinsights/v2/search?org_id=68fed0e32e05b5fd95ec84b9&app_id=app_m6QlDpdMHTBTjmz8aWUJXaoy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()

            if (data.tableRow && data.tableRow.length > 0) {
                setQuestions(data.tableRow)

                // Check if any question has a previous trainee_answer
                const hasPreviousAnswers = data.tableRow.some(q =>
                    q.trainee_answer && q.trainee_answer.trim() !== ""
                )
                setHasPreviousAttempt(hasPreviousAnswers)

                // Check if course date has passed using the first question's course_date
                const firstQuestion = data.tableRow[0];
                if (firstQuestion.course_date) {
                    const isExpired = checkCourseDate(firstQuestion.course_date);
                    setCourseDateExpired(isExpired);
                }

                // Initialize form data with questions
                const initialFormData = {
                    traineeName: "",
                    traineeId: ""
                }

                // Auto-populate traineeName if found in any question
                let foundName = ""
                let foundcourse_id = ""
                data.tableRow.forEach((question) => {
                    // Check for name in various possible fields
                    if (question.name && question.name.trim() !== "") {
                        foundName = question.name
                    } else if (question.trainee_name && question.trainee_name.trim() !== "") {
                        foundName = question.trainee_name
                    } else if (question.student_name && question.student_name.trim() !== "") {
                        foundName = question.student_name
                    }

                    if (question.course_id !== "") {
                        foundcourse_id = question.course_id
                    }
                })

                initialFormData.traineeName = foundName
                initialFormData.traineeId = id // Use the ID from URL as trainee ID
                initialFormData.course_id = foundcourse_id
                // Pre-fill answers if they exist
                data.tableRow.forEach((q, index) => {
                    initialFormData[`question${index + 1}`] = q.trainee_answer || ""
                })

                setFormData(initialFormData)

                // Get passage using the passage ID from the first question
                if (data.tableRow[0].passage) {
                    fetchPassage(data.tableRow[0].passage)
                } else {
                    setError("No passage ID found")
                    setLoading(false)
                }
            } else {
                setError("No questions found")
                setLoading(false)
            }
        } catch (err) {
            console.error("Error fetching questions:", err)
            setError("Failed to load questions. Please try again later.")
            setLoading(false)
        }
    }

    // Fetch passage using passage ID
    const fetchPassage = async (passageId) => {
        try {
            const payload = {
                "target": "pccpassage",
                "action": "search",
                "filters": {
                    "logicalOperator": "AND",
                    "rules": [
                        {
                            "field": "recordId",
                            "dataType": "tag",
                            "comparisonOperator": "equals",
                            "value": passageId
                        }
                    ]
                },
                "columnsToRetrieve": [],
                "resultDisplayOrder": "row",
                "maxRowsPerPage": 10,
                "pageToRetrieve": 1
            }

            const response = await fetch("https://stg-bex.coolriots.ai/bexo/bexinsights/v2/search?org_id=68fed0e32e05b5fd95ec84b9&app_id=app_m6QlDpdMHTBTjmz8aWUJXaoy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()

            if (data.tableRow && data.tableRow.length > 0) {
                setPassageData(data.tableRow[0])
            } else {
                setError("No passage found")
            }
        } catch (err) {
            console.error("Error fetching passage:", err)
            setError("Failed to load passage. Please try again later.")
        } finally {
            setLoading(false)
        }
    }

    // TASK 1: Update trainee_answer with user input and console.log
    const updateQuestionsWithAnswers = () => {
        const updatedQuestions = questions.map((question, index) => {
            return {
                ...question,
                trainee_answer: formData[`question${index + 1}`] || ""
            }
        })

        // Console.log the answers as requested
        console.log("Updated questions with trainee answers:", updatedQuestions)

        return updatedQuestions
    }

    // TASK 2: Send updated response to assistant API with full passage included
    // TASK 2: Send updated response to assistant API with full passage included
    const sendToAssistantAPI = async (updatedQuestions) => {
        try {
            // Create context object with trainee_input and full_passage
            const contextData = {
                trainee_input: JSON.stringify(updatedQuestions),
                full_passage: passageData?.passage || "",
                passage_title: passageData?.title || "",
                trainee_name: formData.traineeName,
                trainee_id: formData.traineeId,
                course_id: formData.course_id
            }

            const payload = {
                "org_id": "68fed0e32e05b5fd95ec84b9",
                "sub_org_id": "68fed0fb2e05b5fd95ec84c3",
                "assistant_name": "697999a402de817e669d4f01",
                "mode": "Specific",
                "opcode_id": "1196a9a36d452f8f1851dc48",
                "context": contextData,
                "stream": false,
                "stream_cumulative": false,
                "emit_token_events": false,
                "stream_buffer_sentences": false
            }

            console.log("Sending to Assistant API:", payload)

            const response = await fetch("https://stg-bex.coolriots.ai/projecto/execute/api/v2/assistant/execute", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            })

            console.log("Assistant API response status:", response.status)

            if (!response.ok) {
                let errorMessage = `Assistant API error! Status: ${response.status}`
                try {
                    const errorText = await response.text()
                    console.error("Assistant API error response text:", errorText)
                    errorMessage += ` - ${errorText}`
                } catch (e) {
                    console.error("Could not read error response")
                }
                throw new Error(errorMessage)
            }

            // Get the response as text (it's a stream of JSON objects)
            const responseText = await response.text()
            console.log("Raw Assistant API response text:", responseText)

            // Split by newlines to handle streaming response
            const jsonObjects = responseText.trim().split('\n').filter(line => line.trim() !== '')

            let finalResult = null

            // Parse each JSON object
            for (const jsonStr of jsonObjects) {
                try {
                    const obj = JSON.parse(jsonStr)
                    console.log("Parsed chunk:", obj)

                    // Look for the final result object
                    if (obj.type === "result" && obj.ok === true) {
                        finalResult = obj

                        // Parse the nested result string as JSON
                        if (obj.output && obj.output.result) {
                            try {
                                finalResult.parsedResult = JSON.parse(obj.output.result)
                                console.log("Parsed final result:", finalResult.parsedResult)
                            } catch (parseError) {
                                console.error("Could not parse nested result JSON:", parseError)
                            }
                        }
                    }
                } catch (parseError) {
                    console.error("Failed to parse JSON chunk:", jsonStr, parseError)
                }
            }

            if (!finalResult) {
                console.warn("No final result found in response, using last object")
                try {
                    finalResult = JSON.parse(jsonObjects[jsonObjects.length - 1])
                } catch (e) {
                    throw new Error("Could not parse any valid JSON from response")
                }
            }

            return finalResult
        } catch (err) {
            console.error("Error sending to assistant API:", err)
            throw err
        }
    }

    // TASK 3: Send updated records to bex-action API
    const sendToBexActionAPI = async (updatedQuestions) => {
        try {
            // Create ChildRecords array based on number of questions
            const childRecords = updatedQuestions.map(question => ({
                "recordId": question.recordId,
                "recordStatus": "updated",
                "id": String(question.id),
                "suggested_answer": question.suggested_answer || "",
                "trainee_answer": question.trainee_answer || ""
            }))

            const payload = {
                "Meta": {
                    "Child": {
                        "Name": "PCC Answer",
                        "Type": "Child Create"
                    },
                    "Action": "update"
                },
                "ChildRecords": childRecords
            }

            console.log("Sending to Bex Action API:", payload)

            const response = await fetch("https://stg-bex.coolriots.ai/bexo/actions/bex-action?org_id=68fed0e32e05b5fd95ec84b9&app_id=app_m6QlDpdMHTBTjmz8aWUJXaoy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                throw new Error(`Bex Action API error! status: ${response.status}`)
            }

            const data = await response.json()
            console.log("Bex Action API response:", data)
            return data
        } catch (err) {
            console.error("Error sending to Bex Action API:", err)
            throw err
        }
    }

    useEffect(() => {
        fetchQuestions()
    }, [])

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Don't submit if there's a previous attempt
        if (hasPreviousAttempt) {
            return
        }

        // Don't submit if course date has passed
        if (courseDateExpired) {
            return
        }

        // Validate all fields are filled (except traineeName and traineeId which are auto-populated)
        const allFieldsFilled = Object.entries(formData).every(([key, value]) => {
            // Skip validation for traineeName and traineeId if they're empty (auto-populated)
            if (key === 'traineeName' || key === 'traineeId') {
                return true
            }
            return value !== ""
        })

        if (allFieldsFilled) {
            try {
                setSubmitting(true); // Start loading

                // TASK 1: Update questions with trainee answers
                const updatedQuestions = updateQuestionsWithAnswers()

                // TASK 2: Send to assistant API (with full passage)
                await sendToAssistantAPI(updatedQuestions)

                // TASK 3: Send to bex-action API
                await sendToBexActionAPI(updatedQuestions)

                // Set submitted state if all APIs succeed
                setSubmitted(true)

            } catch (error) {
                console.error("Error during submission:", error)
                alert("There was an error submitting your answers. Please try again.")
            } finally {
                setSubmitting(false); // Stop loading regardless of success/error
            }
        } else {
            alert("Please fill in all answer fields before submitting.")
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl border-2">
                    <CardContent className="p-8">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                            <p className="text-lg font-medium text-gray-700">Loading assessment...</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl border-2">
                    <CardContent className="p-8">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-lg font-medium text-gray-700">{error}</p>
                            <Button onClick={() => window.location.reload()} className="mt-4">
                                Try Again
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    // Show course date expired message
    if (courseDateExpired) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl border-2">
                    <CardContent className="p-8">
                        <div className="flex flex-col items-center justify-center space-y-6">
                            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                                <svg className="h-8 w-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-gray-900">Course Access Expired</h2>
                                <p className="mt-2 text-gray-600">
                                    The course end date ({questions[0]?.course_date || "N/A"}) has passed.
                                </p>
                                <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium">You will not be able to access this form.</p>
                                        <p className="mt-2">Please contact the NATC team for further assistance.</p>
                                        <div className="mt-4 bg-white p-3 rounded border">
                                            <p className="font-medium">Contact Information:</p>
                                            <p className="mt-1">Email: studentsupport@natcinstitute.edu.sg</p>
                                            <p>Phone: +65 6440 3764</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    // Show only the "Previous Attempt Detected" message if already attempted
    if (hasPreviousAttempt) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl border-2">
                    <CardContent className="p-8">
                        <div className="flex flex-col items-center justify-center space-y-6">
                            <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center">
                                <svg className="h-8 w-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-gray-900">Previous Attempt Detected</h2>
                                <p className="mt-2 text-gray-600">
                                    You have already attempted this assessment.
                                </p>
                                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                    <div className="text-sm text-yellow-800">
                                        <p className="font-medium">Assessment Details:</p>
                                        <p className="mt-1">Trainee: {formData.traineeName || "Not specified"}</p>
                                        <p>Trainee ID: {formData.traineeId}</p>
                                        <p className="mt-2">Course Date: {questions[0]?.course_date || "N/A"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-2xl border-2">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
                            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <CardTitle className="text-2xl">Assessment Submitted</CardTitle>
                        <CardDescription className="mt-2">Your answers have been recorded and will be reviewed.</CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <Button
                            onClick={() => window.location.reload()}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                        >
                            Back to Assessment
                        </Button>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <Card className="border-2 shadow-lg">
                    <CardContent className="pt-6">
                        {/* Course Date Banner */}
                        {questions[0]?.course_date && (
                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="font-medium text-blue-800">Course Date: {questions[0]?.course_date}</span>
                                    </div>
                                    <div className="text-sm text-blue-600">
                                        Form accessible until end of course date
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Trainee Information Section */}
                            <div className="space-y-4 pb-6 border-b-2 border-blue-100">
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                        <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Trainee Information</h3>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="traineeName">Full Name *</Label>
                                        <Input
                                            id="traineeName"
                                            placeholder="Enter your full name"
                                            value={formData.traineeName}
                                            onChange={(e) => setFormData({ ...formData, traineeName: e.target.value })}
                                            disabled={formData.traineeName.trim() !== ""}
                                            className={`border-gray-300 focus:border-blue-500 focus:ring-blue-500 ${formData.traineeName.trim() !== "" ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        />
                                        {formData.traineeName.trim() !== "" && (
                                            <p className="text-xs text-gray-500">Name auto-populated from records</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="traineeId">Trainee ID *</Label>
                                        <Input
                                            id="traineeId"
                                            placeholder="Enter your trainee ID"
                                            value={formData.traineeId}
                                            onChange={(e) => setFormData({ ...formData, traineeId: e.target.value })}
                                            disabled={true}
                                            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 bg-gray-100 cursor-not-allowed"
                                        />
                                        <p className="text-xs text-gray-500">ID from assessment URL</p>
                                    </div>
                                </div>
                            </div>

                            {/* Passage Section */}
                            <div className="space-y-4 pb-6 border-b-2 border-indigo-100">
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                                        <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Reading Passage</h3>
                                </div>
                                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                                    <p className="text-sm font-medium text-amber-800 mb-2">📖 Instructions</p>
                                    <p className="text-sm text-amber-700">
                                        Read the passage carefully and answer the questions below based on your understanding.
                                    </p>
                                </div>
                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-lg border-2 border-indigo-200">
                                    {passageData && (
                                        <>
                                            <h4 className="text-lg font-semibold text-indigo-900 mb-3">
                                                {passageData.title}
                                            </h4>
                                            <p className="text-foreground leading-relaxed text-pretty whitespace-pre-line">
                                                {passageData.passage}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Questions Section */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                        <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">Answer the Following Questions</h3>
                                </div>

                                {questions.map((question, index) => (
                                    <div key={question.recordId || index} className="space-y-2">
                                        <Label htmlFor={`question${index + 1}`} className="text-base font-medium text-gray-900">
                                            {index + 1}. {question.question} *
                                        </Label>
                                        <Textarea
                                            id={`question${index + 1}`}
                                            placeholder="Type your answer here..."
                                            value={formData[`question${index + 1}`] || ""}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    [`question${index + 1}`]: e.target.value,
                                                })
                                            }
                                            rows={4}
                                            required
                                            className="resize-none border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end pt-4">
                                <Button
                                    type="submit"
                                    size="lg"
                                    disabled={submitting || courseDateExpired}
                                    className={`bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {submitting ? (
                                        <>
                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Response'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}